// src/pages/AdminCertificados.js
//
// Panel admin del sistema de certificados.
// URL: /admin/certificados
//
// Tres tabs:
//   1. Emitidos      — listado paginado con filtros (estado, búsqueda,
//                       curso). Botones aprobar/revocar, ver verificaciones.
//   2. Plantillas    — CRUD de plantillas (nombre, estilo, logo, firma,
//                       colores, firmante). Marcado por default por scope.
//   3. Configuración — editar claves de configuracion_sistema
//                       (dominio del QR, umbral de calificación, flag
//                       emitir sin calificación).

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Container, Paper, Typography, Tabs, Tab, TextField, Select,
  MenuItem, FormControl, InputLabel, Button, Table, TableBody,
  TableCell, TableHead, TableRow, Chip, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Stack,
  IconButton, Divider, Pagination, Tooltip, Switch, FormControlLabel,
} from '@mui/material';
import {
  CheckCircle, Block, Visibility, Add, Edit, Delete, Save,
  Refresh, WorkspacePremium, Settings, Palette,
} from '@mui/icons-material';
import { certificadoService, cursoService } from '../services/api';
import ImagenUploader from '../components/common/ImagenUploader';

export default function AdminCertificados() {
  const [tab, setTab] = useState(0);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <WorkspacePremium sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" sx={{ fontWeight: 600 }}>Certificados</Typography>
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab icon={<WorkspacePremium />} iconPosition="start" label="Emitidos" />
          <Tab icon={<Palette />}          iconPosition="start" label="Plantillas" />
          <Tab icon={<Settings />}         iconPosition="start" label="Configuración" />
        </Tabs>
      </Paper>

      {tab === 0 && <TabEmitidos />}
      {tab === 1 && <TabPlantillas />}
      {tab === 2 && <TabConfiguracion />}
    </Container>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// TAB 1: EMITIDOS
// ═══════════════════════════════════════════════════════════════════════
function TabEmitidos() {
  const [filtros, setFiltros] = useState({ estado: '', busqueda: '', cursoId: '', pagina: 1, tamano: 25 });
  const [data, setData] = useState({ loading: true, total: 0, items: [], error: '' });
  const [cursos, setCursos] = useState([]);
  const [dialogRevocar, setDialogRevocar] = useState(null);  // { id, codigo, estudianteNombre }
  const [motivoRevocar, setMotivoRevocar] = useState('');
  const [dialogVerif, setDialogVerif] = useState(null);      // { cert, verificaciones }

  // Carga de cursos para el <Select> de filtro. Se trae hasta 200 cursos
  // (suficientes para cualquier tenant realista). Si crece más, se puede
  // migrar a un autocomplete con búsqueda server-side.
  useEffect(() => {
    (async () => {
      try {
        const resp = await cursoService.getAll({ limit: 200 });
        setCursos(resp.data || []);
      } catch { /* silencioso — el filtro queda sin opciones pero el tab funciona */ }
    })();
  }, []);

  const cargar = useCallback(async () => {
    setData((d) => ({ ...d, loading: true }));
    try {
      const resp = await certificadoService.listarAdmin(filtros);
      setData({ loading: false, ...resp, error: '' });
    } catch (err) {
      setData({ loading: false, total: 0, items: [], error: err.message || 'Error cargando certificados.' });
    }
  }, [filtros]);

  useEffect(() => { cargar(); }, [cargar]);

  const aprobar = async (id) => {
    if (!window.confirm('¿Aprobar este certificado retenido?')) return;
    try { await certificadoService.aprobar(id); await cargar(); }
    catch (err) { alert('No se pudo aprobar: ' + err.message); }
  };

  const confirmarRevocar = async () => {
    if (!dialogRevocar) return;
    try {
      await certificadoService.revocar(dialogRevocar.id, motivoRevocar);
      setDialogRevocar(null);
      setMotivoRevocar('');
      await cargar();
    } catch (err) { alert('No se pudo revocar: ' + err.message); }
  };

  const verVerificaciones = async (cert) => {
    try {
      const verificaciones = await certificadoService.verificacionesAdmin(cert.id);
      setDialogVerif({ cert, verificaciones });
    } catch (err) { alert('Error: ' + err.message); }
  };

  return (
    <Paper sx={{ p: 2 }}>
      {/* Filtros */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Estado</InputLabel>
          <Select label="Estado" value={filtros.estado}
                  onChange={(e) => setFiltros({ ...filtros, estado: e.target.value, pagina: 1 })}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="VALIDO">Válido</MenuItem>
            <MenuItem value="RETENIDO">Retenido</MenuItem>
            <MenuItem value="REVOCADO">Revocado</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Curso</InputLabel>
          <Select label="Curso" value={filtros.cursoId}
                  onChange={(e) => setFiltros({ ...filtros, cursoId: e.target.value, pagina: 1 })}>
            <MenuItem value="">Todos los cursos</MenuItem>
            {cursos.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.title || c.titulo}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField size="small" fullWidth label="Buscar por nombre, curso o código"
                   value={filtros.busqueda}
                   onChange={(e) => setFiltros({ ...filtros, busqueda: e.target.value, pagina: 1 })} />
        <Button startIcon={<Refresh />} onClick={cargar} disabled={data.loading}>Actualizar</Button>
      </Stack>

      {data.error && <Alert severity="error" sx={{ mb: 2 }}>{data.error}</Alert>}

      {data.loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : data.items.length === 0 ? (
        <Alert severity="info">No hay certificados con los filtros actuales.</Alert>
      ) : (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Estudiante</TableCell>
                <TableCell>Curso</TableCell>
                <TableCell>Calificación</TableCell>
                <TableCell>Emitido</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.items.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{c.codigo}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{c.estudianteNombre}</Typography>
                    {c.estudianteEmail && (
                      <Typography variant="caption" color="text.secondary">{c.estudianteEmail}</Typography>
                    )}
                  </TableCell>
                  <TableCell>{c.cursoTitulo}</TableCell>
                  <TableCell>{c.calificacion != null ? Number(c.calificacion).toFixed(1) : '—'}</TableCell>
                  <TableCell>
                    {new Date(c.fechaEmision).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </TableCell>
                  <TableCell>
                    <Chip label={c.estado} size="small"
                          color={c.estado === 'VALIDO' ? 'success' : c.estado === 'RETENIDO' ? 'warning' : 'error'} />
                    {c.motivoEstado && (
                      <Tooltip title={c.motivoEstado}>
                        <Typography variant="caption" color="text.secondary"
                                    sx={{ display: 'block', maxWidth: 200, overflow: 'hidden',
                                          textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.motivoEstado}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Ver verificaciones">
                      <IconButton size="small" onClick={() => verVerificaciones(c)}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Ver público">
                      <IconButton size="small" onClick={() => window.open(`/cert/${c.codigo}`, '_blank')}>
                        <WorkspacePremium fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {c.estado === 'RETENIDO' && (
                      <Tooltip title="Aprobar">
                        <IconButton size="small" color="success" onClick={() => aprobar(c.id)}>
                          <CheckCircle fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {c.estado !== 'REVOCADO' && (
                      <Tooltip title="Revocar">
                        <IconButton size="small" color="error"
                                    onClick={() => setDialogRevocar({ id: c.id, codigo: c.codigo, estudianteNombre: c.estudianteNombre })}>
                          <Block fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {data.total} certificado{data.total === 1 ? '' : 's'}
            </Typography>
            <Pagination
              count={Math.max(1, Math.ceil(data.total / filtros.tamano))}
              page={filtros.pagina}
              onChange={(_, p) => setFiltros({ ...filtros, pagina: p })}
              size="small"
            />
          </Box>
        </>
      )}

      {/* Dialog revocar */}
      <Dialog open={!!dialogRevocar} onClose={() => setDialogRevocar(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Revocar certificado</DialogTitle>
        <DialogContent>
          {dialogRevocar && (
            <Box sx={{ pt: 1 }}>
              <Typography sx={{ mb: 2 }}>
                Vas a revocar el certificado <strong>{dialogRevocar.codigo}</strong> de <strong>{dialogRevocar.estudianteNombre}</strong>.
                Esta acción no es reversible desde la UI (requiere actualización SQL para reactivar).
              </Typography>
              <TextField label="Motivo de la revocación" fullWidth multiline minRows={3}
                         value={motivoRevocar} onChange={(e) => setMotivoRevocar(e.target.value)}
                         placeholder="Ej: fraude académico detectado, datos erróneos, revocación a petición del titular, etc." />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogRevocar(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={confirmarRevocar}>Revocar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog verificaciones */}
      <Dialog open={!!dialogVerif} onClose={() => setDialogVerif(null)} maxWidth="md" fullWidth>
        <DialogTitle>Verificaciones de {dialogVerif?.cert?.codigo}</DialogTitle>
        <DialogContent>
          {dialogVerif && dialogVerif.verificaciones.length === 0 ? (
            <Alert severity="info">Este certificado aún no ha sido escaneado.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha y hora</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>User Agent</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dialogVerif?.verificaciones.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell>{new Date(v.fechaHora).toLocaleString('es-CO')}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{v.ip || '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', maxWidth: 400, overflow: 'hidden',
                                     textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {v.userAgent || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogVerif(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// TAB 2: PLANTILLAS
// ═══════════════════════════════════════════════════════════════════════
function TabPlantillas() {
  const [state, setState] = useState({ loading: true, items: [], error: '' });
  const [editing, setEditing] = useState(null);   // 'new' | plantilla

  const cargar = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const items = await certificadoService.listarPlantillas();
      setState({ loading: false, items, error: '' });
    } catch (err) {
      setState({ loading: false, items: [], error: err.message });
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const eliminar = async (p) => {
    if (!window.confirm(`¿Eliminar la plantilla "${p.nombre}"?`)) return;
    try { await certificadoService.eliminarPlantilla(p.id); await cargar(); }
    catch (err) { alert(err.message); }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Plantillas disponibles</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setEditing('new')}>
          Nueva plantilla
        </Button>
      </Box>

      {state.error && <Alert severity="error" sx={{ mb: 2 }}>{state.error}</Alert>}

      {state.loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Estilo</TableCell>
              <TableCell>Alcance</TableCell>
              <TableCell>Color</TableCell>
              <TableCell>Firmante</TableCell>
              <TableCell>Default</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {state.items.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell>{p.nombre}</TableCell>
                <TableCell><Chip label={p.estilo} size="small" /></TableCell>
                <TableCell>{p.institucionId ? 'Institución' : 'Global (tenant)'}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, bgcolor: p.colorPrincipal || '#999',
                               borderRadius: '50%', border: '1px solid #ccc' }} />
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {p.colorPrincipal || '—'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {p.firmanteNombre ? (
                    <Box>
                      <Typography variant="body2">{p.firmanteNombre}</Typography>
                      {p.firmanteCargo && (
                        <Typography variant="caption" color="text.secondary">{p.firmanteCargo}</Typography>
                      )}
                    </Box>
                  ) : '—'}
                </TableCell>
                <TableCell>
                  {p.esDefault && <Chip label="Default" size="small" color="primary" />}
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(p)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => eliminar(p)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {editing && (
        <DialogPlantilla
          plantilla={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await cargar(); }}
        />
      )}
    </Paper>
  );
}

function DialogPlantilla({ plantilla, onClose, onSaved }) {
  // Estado interno:
  //  - plantillaId: null hasta que se crea. Para plantillas nuevas, se
  //    convierte en el id devuelto por el POST, lo que habilita los
  //    uploaders de logo y firma (que necesitan el id en la URL).
  //  - previewLogo / previewFirma: URL firmada temporal devuelta por el
  //    endpoint de upload, para mostrar la imagen inmediatamente sin
  //    esperar a refrescar el padre.
  const [plantillaId, setPlantillaId] = useState(plantilla?.id || null);
  const [form, setForm] = useState({
    nombre: plantilla?.nombre || '',
    estilo: plantilla?.estilo || 'formal',
    firmanteNombre: plantilla?.firmanteNombre || '',
    firmanteCargo: plantilla?.firmanteCargo || '',
    colorPrincipal: plantilla?.colorPrincipal || '#1a3d2e',
    esDefault: plantilla?.esDefault || false,
    institucionId: plantilla?.institucionId || null,
  });
  const [previewLogo, setPreviewLogo]   = useState(plantilla?.logoUrl || '');
  const [previewFirma, setPreviewFirma] = useState(plantilla?.firmaUrl || '');
  const [saving, setSaving]  = useState(false);
  const [uploading, setUploading] = useState(null);  // 'logo' | 'firma' | null
  const [error, setError]    = useState('');
  const [mensaje, setMensaje] = useState('');

  const esNueva = !plantillaId;

  // Al guardar: si es nueva, POST crea y entra en modo edición
  // (dialog queda abierto, aparecen uploaders). Si ya existe, PUT.
  const guardar = async () => {
    if (!form.nombre.trim()) { setError('El nombre es requerido.'); return; }
    setSaving(true); setError('');
    try {
      if (esNueva) {
        const resp = await certificadoService.crearPlantilla(form);
        setPlantillaId(resp.id);
        setMensaje('Plantilla creada. Ya puedes subir el logo y la firma.');
      } else {
        await certificadoService.actualizarPlantilla(plantillaId, form);
        setMensaje('Cambios guardados.');
      }
    } catch (err) {
      setError(err.message);
    } finally { setSaving(false); }
  };

  const subirImagen = async (tipo, file) => {
    if (!plantillaId) {
      setError('Primero guarda los datos básicos para habilitar la subida.');
      return;
    }
    if (!file) return;
    setUploading(tipo); setError(''); setMensaje('');
    try {
      const fn = tipo === 'logo' ? certificadoService.subirLogoPlantilla : certificadoService.subirFirmaPlantilla;
      const { signedUrl } = await fn(plantillaId, file);
      if (tipo === 'logo') setPreviewLogo(signedUrl);
      else                 setPreviewFirma(signedUrl);
      setMensaje(`${tipo === 'logo' ? 'Logo' : 'Firma'} subido correctamente.`);
    } catch (err) {
      setError(err.message || 'Error subiendo el archivo.');
    } finally { setUploading(null); }
  };

  const cerrarYRefrescar = () => { onSaved(); };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{esNueva ? 'Nueva plantilla' : 'Editar plantilla'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error   && <Alert severity="error"   onClose={() => setError('')}>{error}</Alert>}
          {mensaje && <Alert severity="success" onClose={() => setMensaje('')}>{mensaje}</Alert>}

          <TextField label="Nombre" fullWidth autoFocus
                     value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
          <FormControl fullWidth>
            <InputLabel>Estilo visual</InputLabel>
            <Select label="Estilo visual" value={form.estilo}
                    onChange={(e) => setForm({ ...form, estilo: e.target.value })}>
              <MenuItem value="formal">Formal académico (clásico, serif, bordes dorados)</MenuItem>
              <MenuItem value="moderno">Moderno (banda de color, sans-serif)</MenuItem>
            </Select>
          </FormControl>

          {/* Uploaders — habilitados solo cuando la plantilla ya existe */}
          <Divider>Imágenes</Divider>
          {esNueva && (
            <Alert severity="info">
              Los campos de logo y firma se habilitan una vez guardes los datos básicos.
            </Alert>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <ImagenUploader
              label="Logo de la institución"
              preview={previewLogo}
              disabled={esNueva || uploading === 'logo'}
              uploading={uploading === 'logo'}
              onPick={(file) => subirImagen('logo', file)}
              onClear={() => setPreviewLogo('')}
              fondo="#f5f5f5"
              hint="PNG o SVG con fondo transparente idealmente. Máx 10 MB."
            />
            <ImagenUploader
              label="Firma del firmante"
              preview={previewFirma}
              disabled={esNueva || uploading === 'firma'}
              uploading={uploading === 'firma'}
              onPick={(file) => subirImagen('firma', file)}
              onClear={() => setPreviewFirma('')}
              fondo="#ffffff"
              hint="PNG con fondo transparente idealmente."
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Firmante — nombre" fullWidth
                       value={form.firmanteNombre} onChange={(e) => setForm({ ...form, firmanteNombre: e.target.value })} />
            <TextField label="Firmante — cargo" fullWidth
                       value={form.firmanteCargo} onChange={(e) => setForm({ ...form, firmanteCargo: e.target.value })} />
          </Stack>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TextField label="Color principal" type="color" sx={{ width: 180 }}
                       value={form.colorPrincipal} onChange={(e) => setForm({ ...form, colorPrincipal: e.target.value })} />
            <TextField label="Color (hex)" sx={{ width: 140 }}
                       value={form.colorPrincipal} onChange={(e) => setForm({ ...form, colorPrincipal: e.target.value })}
                       InputProps={{ sx: { fontFamily: 'monospace' } }} />
            <FormControlLabel sx={{ ml: 2 }}
              control={<Switch checked={form.esDefault}
                       onChange={(e) => setForm({ ...form, esDefault: e.target.checked })} />}
              label="Usar por defecto" />
          </Box>

          <Alert severity="info">
            Si marcas <strong>Usar por defecto</strong>, cualquier otra plantilla con el mismo alcance
            (global o la misma institución) perderá automáticamente ese estado.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {esNueva ? 'Cancelar' : 'Cerrar sin aplicar'}
        </Button>
        <Button variant="contained" startIcon={<Save />} onClick={guardar} disabled={saving}>
          {saving ? 'Guardando…' : (esNueva ? 'Crear y continuar' : 'Guardar cambios')}
        </Button>
        {!esNueva && (
          <Button variant="contained" color="success" onClick={cerrarYRefrescar}>
            Hecho
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ─── Uploader de imagen con preview ────────────────────────────────────
// ImagenUploader movido a src/components/common/ImagenUploader.js

// ═══════════════════════════════════════════════════════════════════════
// TAB 3: CONFIGURACIÓN
// ═══════════════════════════════════════════════════════════════════════
function TabConfiguracion() {
  const [state, setState] = useState({ loading: true, items: [], error: '' });
  const [editandoClave, setEditandoClave] = useState(null);
  const [valorEdit, setValorEdit] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const items = await certificadoService.getConfiguracion();
      setState({ loading: false, items, error: '' });
    } catch (err) {
      setState({ loading: false, items: [], error: err.message });
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const empezarEdicion = (item) => {
    setEditandoClave(item.clave);
    setValorEdit(item.valor);
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      await certificadoService.actualizarConfig(editandoClave, valorEdit);
      setEditandoClave(null);
      await cargar();
    } catch (err) { alert('Error: ' + err.message); }
    finally { setGuardando(false); }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Configuración del sistema de certificados</Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Estos valores se aplican a todos los certificados emitidos en este tenant. Los cambios tienen efecto
        inmediato, sin necesidad de reiniciar el backend.
      </Alert>

      {state.error && <Alert severity="error" sx={{ mb: 2 }}>{state.error}</Alert>}

      {state.loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <Stack spacing={2}>
          {state.items.map((item) => (
            <Paper key={item.clave} variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                    {item.clave}
                  </Typography>
                  {item.descripcion && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {item.descripcion}
                    </Typography>
                  )}
                </Box>
                {editandoClave !== item.clave && (
                  <Button size="small" startIcon={<Edit />} onClick={() => empezarEdicion(item)}>
                    Editar
                  </Button>
                )}
              </Box>

              <Divider sx={{ my: 1.5 }} />

              {editandoClave === item.clave ? (
                <Box>
                  <TextField fullWidth autoFocus value={valorEdit}
                             onChange={(e) => setValorEdit(e.target.value)}
                             InputProps={{ sx: { fontFamily: 'monospace' } }} />
                  <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
                    <Button onClick={() => setEditandoClave(null)} disabled={guardando}>Cancelar</Button>
                    <Button variant="contained" startIcon={<Save />} onClick={guardar} disabled={guardando}>
                      {guardando ? 'Guardando…' : 'Guardar'}
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ fontFamily: 'monospace',
                            bgcolor: 'action.hover', px: 1.5, py: 1, borderRadius: 1 }}>
                  {item.valor}
                </Typography>
              )}
            </Paper>
          ))}
        </Stack>
      )}
    </Paper>
  );
}
